import React, { useReducer, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSolarPanel, faBolt, faBatteryFull, faCheck, faLayerGroup, faWallet, faListCheck, faRotateLeft, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import IkonaAsystenta from '../components/ikony/IkonaAsystenta';
import BackButton from '../components/BackButton';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { zakresFalownika } from '../lib/falownik';
import CategorySection from '../components/kits/CategorySection';
import WybraneModuly from '../components/kits/WybraneModuly';
import VariantCard from '../components/kits/VariantCard';
import NoResults from '../components/kits/NoResults';
import PreferenceCostsNote from '../components/kits/PreferenceCostsNote';
import ProgressRail from '../components/kits/ProgressRail';
import WyborDrogi from '../components/kreator/WyborDrogi';
import ZestawWedlugPreferencji from '../components/kits/ZestawWedlugPreferencji';
import CheckBox from '../components/kits/CheckBox';
import BudgetSlider from '../components/kits/BudgetSlider';
import RecommendedProduct from '../components/kits/RecommendedProduct';
import KrokKonstrukcji from '../components/kits/KrokKonstrukcji';
import { konstrukcjaWg, kosztKonstrukcji } from '../lib/konstrukcja';
import { kosztMontazu } from '../config/cennikMontazu';
import { odczytajDaneProjektu, zapiszDaneProjektu } from '../lib/daneProjektu';
import { ADRES_PROJEKTU_Z_ASYSTENTA, adresBezZ3D, odczytajZProjektu3D, wczytacZ3D } from '../lib/projekt3d/asystent3d';
import {
  odczytajNazweZestawu, odczytajSesjeAsystenta, wyczyscSesjeAsystenta,
  zapiszNazweZestawu, zapiszSesjeAsystenta,
} from '../lib/sesjaAsystenta';
import { suggestKits } from '../api/kits';
import { recommendPanel, recommendInverter, recommendBattery, przypietyZModelu } from '../api/recommend';
import { getKitPreferences, getKlient } from '../api/account';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  adresBezKlienta, adresKartyKlienta, klientZAdresu, wartosciZKlienta,
} from '../lib/klienci/asystentKlienta';
import { rozwiazSlotyPelne, slotPusty } from '../lib/slotyModulow';
import { useAuth } from '../context/AuthContext';
import { useLimitBezKonta } from '../hooks/useLimitBezKonta';
import OknoRejestracji from '../components/auth/OknoRejestracji';
import { podzielWarianty, TEKSTY_OKNA_DOBORU, ZestawyZaKontem } from '../components/kits/ZestawyBezKonta';
import { pln } from '../components/kits/format';
import { useTranslation, Trans } from 'react-i18next';
import { KRAJ, jestPolska } from '../kraj';

// Zakres i wartość domyślna budżetu w walucie kraju. Polska bez zmian; dla innych
// krajów przeliczamy polskie kwoty kursem orientacyjnym 1 PLN ≈ 1,18 RON
// i zaokrąglamy do pełnych tysięcy (10 000 zł -> 12 000 lei, 100 000 zł -> 120 000 lei,
// 45 000 zł -> 53 000 lei). To tylko granice suwaka, nie przeliczenie cen.
const BUDZET_KRAJU = jestPolska
  ? { min: 10000, max: 100000, domyslny: 45000 }
  : { min: 12000, max: 120000, domyslny: 53000 };
const BUDGET_MIN = BUDZET_KRAJU.min;
const BUDGET_MAX = BUDZET_KRAJU.max;
const clampBudget = (v) => Math.min(BUDGET_MAX, Math.max(BUDGET_MIN, Number(v) || BUDGET_MIN));

// Domyślne fazy falownika z rodzaju instalacji (nadpisywalne w sekcji falownika).
const PHASE_DEFAULT = { '1F': ['1 Fazowy'], '3F': ['3 Fazowy'], null: [] };

const CATEGORY_CARDS = [
  { key: 'panels', icon: faSolarPanel },
  { key: 'inverter', icon: faBolt },
  { key: 'battery', icon: faBatteryFull },
];

const initialState = {
  selected: { panels: true, inverter: true, battery: true },
  budget_total_pln: BUDZET_KRAJU.domyslny,
  budget_any: false,   // „dowolny budżet" -> budget_total_pln: null w payloadzie
  installation_phase: null, // „Nie wiem" domyślnie
  panels: {
    // `tryb_mocy`: 'kwp' — użytkownik podaje moc instalacji; 'sztuki' — podaje
    // liczbę modułów i ich moc, a kWp LICZYMY. Niezależnie od trybu w dół idzie
    // zawsze `target_power_kwp`, więc payload, linia postępu, dobór konstrukcji
    // i zakres falownika nie wiedzą o istnieniu drugiego trybu.
    tryb_mocy: 'kwp', module_count: '',
    target_power_kwp: '', preferred_module_power_w: '', power_tolerance_w: 5, tolerance_direction: 'both',
    // `series_in` NIE jest w MODEABLE (patrz niżej) — zawsze twardy filtr, bez
    // przełącznika required/preferred: seria nie jest kolumną SQL, więc nie da
    // się jej ocenić w scoringu (backend_api/kits/candidates.py).
    filters: { manufacturer_in: [], series_in: [], types_in: [], min_efficiency: '', min_product_warranty: '', module_length_min: '', module_length_max: '', module_thickness_range: '', max_weight: '', frame_colour: '', max_price_per_wp: '', max_price_per_unit: '' },
    // Tryby domyślnie „wymagane" — wpisany filtr ma realnie zawężać dobór (tryb „—" usunięty).
    modes: { manufacturer_in: 'required', types_in: 'required', min_efficiency: 'required', frame_colour: 'required' },
  },
  inverter: {
    power_kw_min: '', power_kw_max: '', power_kw_preferred: '',
    // Czy zakres mocy podstawiliśmy MY z mocy instalacji. Póki tak, podąża za
    // kWp; pierwsza ręczna zmiana odcina go od tej reguły na dobre.
    power_auto: false,
    // Liczba falowników TEGO SAMEGO modelu. Instalacja 100 kW to zwykle dwa
    // falowniki po 50 kW, więc zakres mocy dotyczy pojedynczej sztuki.
    quantity: 1,
    filters: { manufacturer_in: [], series_in: [], types_in: ['Hybrydowy'], phase_in: [], mppt_min: '', min_warranty: '', max_price_per_unit: '' },
    // Rodzaj falownika domyślnie „wymagany" = Hybrydowy (magazyn potrzebuje falownika
    // hybrydowego; wcześniej ta domyślna wartość NIE była wysyłana przez tryb „—").
    modes: { manufacturer_in: 'required', types_in: 'required' },
    owned_model_id: null, owned_label: null,
  },
  battery: {
    capacity_kwh_min: '', capacity_kwh_max: '', capacity_kwh_preferred: '',
    // Margines doboru pojemności — ta sama mechanika co tolerancja mocy przy
    // modułach (wartość + kierunek). Domyślnie 2 kWh w obie strony: pojemności
    // magazynów układają się w skoki co ok. 2,5–3 kWh, więc szukanie „równo
    // 10 kWh" odrzuca sąsiednie moduły tej samej rodziny bez żadnego powodu.
    capacity_tolerance_kwh: 2, tolerance_direction: 'both',
    // Łączenie modułów bateryjnych: zakres pojemności dotyczy wtedy SUMY,
    // a silnik dobiera liczbę sztuk. 10 kWh to częściej dwa moduły po 5 niż
    // jeden dziesięciokilowatowy, i zwykle wychodzi taniej.
    // Łączenie modułów magazynu domyślnie włączone (decyzja właściciela 2026-10-12).
    laczenie: true,
    // „Rodzaj" magazynu usunięty z UI — silnik sam rozstrzyga typ i potrzebę BMS (po napięciu).
    filters: { manufacturer_in: [], series_in: [], voltage_in: [], max_price_per_kwh: '', max_price_per_unit: '' },
    modes: { manufacturer_in: 'required', voltage_in: 'required' },
    owned_model_id: null, owned_label: null,
  },
  constraints: { require_available: true, allow_unknown_compatibility: false, single_seller_only: false },
  // Przypięte produkty z dymków (checkbox „wybierz do zestawu"): panels/inverter -> oferta,
  // battery -> cały pakiet {magazine, bms,...}. model_id oferty trafia do pinned_model_id.
  pinned: { panels: null, inverter: null, battery: null },
};

/**
 * Podstawia zakres mocy falownika wyprowadzony z kWp.
 *
 * Robi to, gdy pola są puste ALBO gdy poprzednia wartość też pochodziła od nas.
 * Bez tego drugiego warunku zakres zostawał przy pierwszej policzonej wartości:
 * zmiana mocy z 7 na 9 kWp zostawiała falownik 5,5–7 kW i po cichu filtrowała
 * po nieaktualnym przedziale. Zmierzone.
 */
const zFalownikiem = (state, kwp) => {
  const tkniety = (state.inverter.power_kw_min || state.inverter.power_kw_max)
    && !state.inverter.power_auto;
  if (tkniety) return state;

  const zakres = zakresFalownika(kwp);
  if (!zakres) return state;
  return {
    ...state,
    inverter: {
      ...state.inverter,
      power_kw_min: String(zakres.min),
      power_kw_max: String(zakres.max),
      power_auto: true,
    },
  };
};

/**
 * Moc instalacji z liczby modułów i mocy jednego z nich.
 *
 * Moduły podaje się w watach, instalację w kilowatach — stąd dzielenie przez
 * 1000. Brak którejkolwiek wartości daje pusty łańcuch, a nie zero: zero
 * przeszłoby walidację „moc podana" i puściło dobór bez mocy.
 */
const kwpZeSztuk = (sztuki, mocW) => {
  const n = Number(sztuki);
  const w = Number(mocW);
  if (!Number.isFinite(n) || n <= 0 || !Number.isFinite(w) || w <= 0) return '';
  // `toFixed(3)` + unary plus ucina ogon zmiennoprzecinkowy (12 × 455 = 5.46,
  // nie 5.460000000000001) i nie zostawia zbędnych zer.
  return String(+((n * w) / 1000).toFixed(3));
};

/**
 * O ile rozszerzyć zakres pojemności magazynu w każdą stronę.
 *
 * Kierunek działa jak przy tolerancji mocy modułów: `both` rozszerza w obie
 * strony, `up` tylko w górę, `down` tylko w dół. Eksportowane, bo używa tego
 * i budowa żądania, i podpowiedź pod polami — dwie kopie tego rachunku
 * rozjechałyby się przy pierwszej zmianie.
 */
export const marginesPojemnosci = (battery) => {
  const m = Number(battery?.capacity_tolerance_kwh);
  if (!Number.isFinite(m) || m <= 0) return { doDolu: 0, doGory: 0 };
  const kierunek = battery?.tolerance_direction || 'both';
  return {
    doDolu: kierunek === 'up' ? 0 : m,
    doGory: kierunek === 'down' ? 0 : m,
  };
};

/** Po każdej zmianie liczby modułów albo ich mocy przelicza kWp i zakres falownika. */
const przeliczZeSztuk = (state) => {
  if (state.panels.tryb_mocy !== 'sztuki') return state;
  const kwp = kwpZeSztuk(state.panels.module_count, state.panels.preferred_module_power_w);
  const next = { ...state, panels: { ...state.panels, target_power_kwp: kwp } };
  return kwp ? zFalownikiem(next, Number(kwp)) : next;
};

/**
 * Wartości z karty klienta (`lib/klienci/asystentKlienta`, `wartosciZKlienta`) w formularzu asystenta.
 *
 * Wypełnia TYLKO puste pola: to, co użytkownik zdążył wpisać, zanim karta klienta się wczytała, wygrywa.
 * Moc idzie tą samą drogą co wpisana ręcznie (zakres falownika z kWp), a pojemność jako zakres od-do równy
 * jednej wartości: w karcie stoi pojemność, jaką klient ma dostać, a margines doboru poszerza ją i tak
 * w obie strony. Sama dolna granica (jak przy `?kwh=` z kalkulatora) wpuszczałaby magazyny dwa razy większe.
 * Tryb „liczba modułów” zostaje nietknięty, bo tam kWp jest liczone, a nie wpisywane.
 */
export const uzupelnijZKlienta = (state, { kwp, kwh } = {}) => {
  let next = state;
  if (kwp && numOrNull(kwp) > 0 && (state.panels.tryb_mocy || 'kwp') === 'kwp'
      && !(numOrNull(state.panels.target_power_kwp) > 0)) {
    next = zFalownikiem({ ...next, panels: { ...next.panels, target_power_kwp: kwp } }, Number(kwp));
  }
  if (kwh && numOrNull(kwh) > 0 && numOrNull(state.battery.capacity_kwh_min) == null
      && numOrNull(state.battery.capacity_kwh_max) == null) {
    next = { ...next, battery: { ...next.battery, capacity_kwh_min: kwh, capacity_kwh_max: kwh } };
  }
  return next;
};

/**
 * Dane z Projektu 3D (trzecia droga asystenta, `lib/projekt3d/asystent3d`): liczba i moc modułów w trybie
 * „liczba modułów” (kWp i zakres falownika liczą się same) i pojemność magazynu jako zakres od-do równy jednej
 * wartości, jak z karty klienta. W przeciwieństwie do karty NADPISUJE pola: ktoś właśnie zbudował model po to,
 * żeby z niego dobierać, więc liczy się to, co policzył model.
 */
export const zProjektu3D = (state, d) => {
  if (!d || !(d.liczbaModulow > 0) || !(d.mocModuluW > 0)) return state;
  let next = {
    ...state,
    selected: { ...state.selected, panels: true },
    panels: { ...state.panels, tryb_mocy: 'sztuki', module_count: String(d.liczbaModulow), preferred_module_power_w: String(d.mocModuluW) },
  };
  next = przeliczZeSztuk(next);
  if (d.magazynKwh > 0) {
    next = { ...next, selected: { ...next.selected, battery: true },
      battery: { ...next.battery, capacity_kwh_min: String(d.magazynKwh), capacity_kwh_max: String(d.magazynKwh) } };
  }
  return next;
};

function reducer(state, action) {
  switch (action.type) {
    case 'REPLACE':
      return action.state;
    case 'PREFILL_3D':
      return zProjektu3D(state, action.dane);
    case 'PREFILL_KLIENT':
      return uzupelnijZKlienta(state, action.wartosci);
    case 'RESET':
      // Świeża kopia stanu początkowego (deep, żeby nie współdzielić zagnieżdżonych obiektów).
      return JSON.parse(JSON.stringify(initialState));
    case 'TOGGLE_CATEGORY':
      return { ...state, selected: { ...state.selected, [action.key]: !state.selected[action.key] } };
    case 'SET_BUDGET':
      return { ...state, budget_total_pln: action.value };
    case 'SET_BUDGET_ANY':
      return { ...state, budget_any: action.value };
    case 'SET_KWP': {
      // Moc falownika wynika z mocy instalacji — bez tego etap „Falownik" zostawał
      // pusty i nigdy nie zapalał się w linii postępu, mimo że użytkownik przez niego
      // przeszedł. Rodzaj falownika ma domyślną wartość, więc brakowało tylko mocy.
      const next = { ...state, panels: { ...state.panels, target_power_kwp: action.value } };
      return zFalownikiem(next, action.value);
    }
    case 'SET_TRYB_MOCY': {
      const next = { ...state, panels: { ...state.panels, tryb_mocy: action.value } };
      // Powrót na „kWp" ZOSTAWIA policzoną wcześniej moc w polu — użytkownik
      // widzi liczbę, którą przed chwilą wyprowadziliśmy z jego modułów,
      // i może ją poprawić, zamiast zaczynać od pustego pola.
      return przeliczZeSztuk(next);
    }
    case 'SET_MODULE_COUNT':
      return przeliczZeSztuk({ ...state, panels: { ...state.panels, module_count: action.value } });
    case 'SET_PHASE':
      return {
        ...state,
        installation_phase: action.value,
        inverter: { ...state.inverter, filters: { ...state.inverter.filters, phase_in: PHASE_DEFAULT[action.value] || [] } },
      };
    case 'SET_FIELD': {
      const next = { ...state, [action.category]: { ...state[action.category], [action.key]: action.value } };
      // Ręczne dotknięcie mocy falownika odłącza go od reguły wyprowadzania z kWp.
      if (action.category === 'inverter' && (action.key === 'power_kw_min' || action.key === 'power_kw_max')) {
        next.inverter = { ...next.inverter, power_auto: false };
      }
      // Moc modułu jest edytowalna w DWÓCH miejscach (sekcja „Moduły PV" i pole
      // przy liczbie sztuk) — to jedno i to samo pole stanu. W trybie „sztuki"
      // każda jego zmiana musi przeliczyć moc instalacji, niezależnie od tego,
      // przez które wejście przyszła.
      if (action.category === 'panels' && action.key === 'preferred_module_power_w') {
        return przeliczZeSztuk(next);
      }
      return next;
    }
    case 'SET_FILTER':
      return { ...state, [action.category]: { ...state[action.category], filters: { ...state[action.category].filters, [action.key]: action.value } } };
    case 'SET_MODE':
      return { ...state, [action.category]: { ...state[action.category], modes: { ...state[action.category].modes, [action.key]: action.mode } } };
    case 'SET_CONSTRAINT':
      return { ...state, constraints: { ...state.constraints, [action.key]: action.value } };
    case 'SET_OWNED':
      return { ...state, [action.category]: { ...state[action.category], owned_model_id: action.device?.id ?? null, owned_label: action.device?.product_name ?? null } };
    case 'SET_PINNED': {
      const next = { ...state, pinned: { ...state.pinned, [action.category]: action.payload } };
      // Wybrany moduł ROZSTRZYGA moc instalacji, gdy liczymy ją ze sztuk.
      // Bez tego 12 sztuk × wybrany moduł 650 W dawało moc z pola „preferowana
      // moc modułu" (albo zero, gdy było puste), a na tym stoi cały proponowany
      // zakres falownika w następnym kroku.
      if (action.category === 'panels' && next.panels.tryb_mocy === 'sztuki') {
        const moc = Number(action.payload?.power);
        if (Number.isFinite(moc) && moc > 0) {
          next.panels = { ...next.panels, preferred_module_power_w: String(moc) };
        }
        return przeliczZeSztuk(next);
      }
      return next;
    }
    default:
      return state;
  }
}

// Poluzowanie jednego warunku (klik w relaksacji przy braku wyników).
// UWAGA: nazwy `constraint` muszą być IDENTYCZNE jak te zwracane przez backend
// (engine._relaxations) — płaskie, nie kropkowane. Wcześniej front oczekiwał nazw
// kropkowanych z mocka, przez co przyciski relaksacji (poza budżetem) nic nie robiły.
function applyRelaxation(state, relax) {
  const { constraint, suggested } = relax;
  const s = JSON.parse(JSON.stringify(state));
  switch (constraint) {
    case 'budget_total_pln': s.budget_total_pln = suggested; break;
    case 'capacity_kwh_min': s.battery.capacity_kwh_min = suggested; break;
    case 'single_seller_only': s.constraints.single_seller_only = false; break;
    case 'require_available': s.constraints.require_available = false; break;
    default: break;
  }
  return s;
}

// ---- Budowa payloadu §1 (front nie liczy — tylko porządkuje pola formularza) ----
const numOrNull = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

/**
 * Jedna linijka parametrów dobranego sterownika BMS — okno napięcia, prąd,
 * temperatura pracy, klasa szczelności.
 *
 * Sterownik nie ma własnej strony produktu (listing i renderer SEO pomijają
 * `type='BMS'`), więc asystent jest JEDYNYM miejscem, gdzie te dane z karty
 * katalogowej mogą się pokazać. Stąd forma skrótowa zamiast tabeli: ma
 * potwierdzić, że proponowany sterownik pasuje do stacku, a nie zastąpić kartę.
 *
 * Pola bywają puste — przy ośmiu na trzynaście kart okno napięcia zależy od
 * liczby modułów i celowo go nie wpisywaliśmy. Każdy człon jest więc opcjonalny,
 * a przy komplecie braków funkcja zwraca null i linia w ogóle się nie renderuje.
 */
const liniaParametrowBms = (spec, t) => {
  const v = spec?.values;
  if (!v) return null;
  const czlony = [];
  if (v.voltage_min_v != null && v.voltage_max_v != null) {
    czlony.push(`${v.voltage_min_v}–${v.voltage_max_v} V`);
  } else if (v.voltage_nominal_v != null) {
    czlony.push(`${v.voltage_nominal_v} V`);
  }
  const prad = v.discharge_current_max_a ?? v.charge_current_max_a;
  if (prad != null) czlony.push(t('kreator.propozycja.bms_prad', { prad }));
  if (v.temp_discharge_min_c != null && v.temp_discharge_max_c != null) {
    czlony.push(`${v.temp_discharge_min_c}…${v.temp_discharge_max_c} °C`);
  }
  if (v.ip_rating) czlony.push(v.ip_rating);
  return czlony.length ? czlony.join(' · ') : null;
};
const MODEABLE = {
  panels: ['manufacturer_in', 'types_in', 'min_efficiency', 'frame_colour'],
  inverter: ['manufacturer_in', 'types_in'],
  battery: ['manufacturer_in', 'voltage_in'],
};
const hasVal = (v) => (Array.isArray(v) ? v.length > 0 : v !== '' && v !== null && v !== undefined);

function buildFilters(category, rawFilters, modeMap) {
  const modeable = MODEABLE[category] || [];
  const filters = {};
  const modes = {};
  Object.entries(rawFilters).forEach(([key, val]) => {
    const isModeable = modeable.includes(key);
    const value = Array.isArray(val) ? val : (typeof val === 'string' && /price|efficiency|warranty|length|weight|mppt/.test(key) ? numOrNull(val) : val);
    if (isModeable) {
      const mode = modeMap[key];
      if (mode && hasVal(value)) { filters[key] = value; modes[key] = mode; }
    } else if (hasVal(value)) {
      filters[key] = value;
    }
  });
  return { filters, modes };
}

/**
 * Ile z budżetu zjadają pozycje, których silnik NIE zna: konstrukcja i montaż.
 *
 * Silnik filtruje zestawy po SWOJEJ sumie (produkty + dostawa), więc żeby budżet
 * podany przez użytkownika znaczył ŁĄCZNY koszt, musimy te dwie pozycje odjąć
 * ZANIM wyślemy zapytanie. Bez tego „budżet 45 tys." oznaczał 45 tys. na sam
 * sprzęt, a rachunek na karcie wychodził grubo powyżej — i była to jedyna liczba,
 * którą użytkownik naprawdę porównuje.
 *
 * Moc modułu na tym etapie NIE JEST jeszcze znana (nie ma wybranego zestawu),
 * więc konstrukcję liczymy dla mocy odniesienia. Różnica wobec finalnej kwoty
 * jest rzędu kilkunastu procent tej pozycji, czyli ułamka procenta budżetu.
 */
function kosztyWlasne(state, konstrukcja, montazWliczony) {
  const kwp = numOrNull(state.panels.target_power_kwp);
  if (!kwp) return 0;
  const k = kosztKonstrukcji({ kwp, konstrukcja });
  return (k?.razemZl || 0) + (montazWliczony ? (kosztMontazu(kwp) || 0) : 0);
}

function buildPayload(state, kosztyDodatkowe = 0) {
  const categories = Object.keys(state.selected).filter((k) => state.selected[k]);
  const p = buildFilters('panels', state.panels.filters, state.panels.modes);
  const i = buildFilters('inverter', state.inverter.filters, state.inverter.modes);
  const b = buildFilters('battery', state.battery.filters, state.battery.modes);
  return {
    categories,
    // „dowolny" => brak limitu budżetu (backend: budget is None -> nie filtruje po sumie)
    // Budżet POMNIEJSZONY o konstrukcję i montaż — patrz `kosztyWlasne`.
    budget_total_pln: state.budget_any
      ? null
      : Math.max(0, (numOrNull(state.budget_total_pln) || 0) - kosztyDodatkowe),
    installation_phase: state.installation_phase,
    panels: {
      target_power_kwp: numOrNull(state.panels.target_power_kwp),
      preferred_module_power_w: numOrNull(state.panels.preferred_module_power_w),
      power_tolerance_w: numOrNull(state.panels.power_tolerance_w),
      tolerance_direction: state.panels.tolerance_direction,
      filters: p.filters, modes: p.modes,
      pinned_model_id: state.pinned?.panels?.model_id ?? null,
    },
    inverter: {
      power_kw_min: numOrNull(state.inverter.power_kw_min),
      power_kw_max: numOrNull(state.inverter.power_kw_max),
      power_kw_preferred: numOrNull(state.inverter.power_kw_preferred),
      quantity: Math.max(1, Number(state.inverter.quantity) || 1),
      filters: i.filters, modes: i.modes,
      owned_model_id: state.inverter.owned_model_id,
      pinned_model_id: state.pinned?.inverter?.model_id ?? null,
    },
    battery: (() => {
      // Dwie warstwy rozszerzenia wpisanego zakresu, obie po stronie WEJŚCIA —
      // front nie liczy wyników, tylko koryguje zapytanie:
      //   * ±0,5 kWh na sztywno, pod potoczne pojemności (5 ≈ 5,12),
      //   * margines doboru z formularza, w wybranym kierunku.
      const { doDolu, doGory } = marginesPojemnosci(state.battery);
      let capMin = numOrNull(state.battery.capacity_kwh_min);
      let capMax = numOrNull(state.battery.capacity_kwh_max);
      if (capMin != null) capMin = Math.max(0, +(capMin - 0.5 - doDolu).toFixed(2));
      if (capMax != null) capMax = +(capMax + 0.5 + doGory).toFixed(2);
      return {
        capacity_kwh_min: capMin,
        capacity_kwh_max: capMax,
        capacity_kwh_preferred: numOrNull(state.battery.capacity_kwh_preferred),
        laczenie: Boolean(state.battery.laczenie),
        filters: b.filters, modes: b.modes,
        owned_model_id: state.battery.owned_model_id,
        pinned_model_id: state.pinned?.battery?.magazine?.model_id ?? null,
      };
    })(),
    constraints: state.constraints,
  };
}

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 animate-pulse">
    <div className="h-5 w-40 bg-gray-100 rounded mb-4" />
    {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-gray-100 rounded-lg mb-2" />)}
    <div className="h-20 bg-gray-100 rounded-xl mt-3" />
  </div>
);

// Etap kreatora: aktywny „wypchnięty do przodu" (cień + ring), pozostałe przygaszone
// „pod spodem" — wciąż klikalne; kliknięcie/focus podnosi etap. „Dalej" przechodzi do
// kolejnego etapu bez wymuszania wypełnienia (defaulty są OK).
// UWAGA: świadomie BEZ transform/scale — ułamkowa skala rozmywała tekst (render
// między pikselami); głębię robią wyłącznie cień, ring i przygaszenie.
const Stage = ({ id, active, onActivate, onNext, className = '', children }) => {
  const { t } = useTranslation('asystent');
  return (
  <div
    onMouseDownCapture={() => onActivate(id)}
    onFocusCapture={() => onActivate(id)}
    className={`rounded-2xl transition-[opacity,box-shadow] duration-300 ease-out ${className} ${
      active
        ? 'relative z-10 opacity-100 shadow-xl shadow-blue-900/10 ring-2 ring-blue-500/40 bg-white'
        : 'opacity-60 shadow-none hover:opacity-85'
    }`}
  >
    {children}
    {active && onNext && (
      <div className="flex justify-end px-4 pb-3 -mt-1">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(id); }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200 hover:border-blue-600 rounded-lg px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {t('kreator.dalej')}
        </button>
      </div>
    )}
  </div>
  );
};

/**
 * Stan początkowy zasilony parametrami z adresu (`?czego=&kwp=&budzet=`).
 *
 * Dzięki temu formularz startowy na stronie głównej realnie ZACZYNA dobór,
 * zamiast tylko przenosić do pustego kreatora. Parametry są nieobowiązkowe
 * i celowo nieliczne — reszta zostaje domyślna, a użytkownik doprecyzuje ją tutaj.
 *
 * Wartości spoza zakresu ignorujemy zamiast odrzucać: adres może zostać wklejony
 * ręcznie albo obcięty, a pusty kreator jest lepszy niż komunikat o błędzie.
 */
/**
 * `przypnij=inwerter-123` (kafelek „Asystent doboru” na stronie produktu, 2026-10-08): ten model trafia
 * do zestawu jako przypięty (`pinned_model_id`, silnik buduje zestaw wokół niego). Sam stan przypięcia
 * ustawia efekt po pobraniu oferty modelu; tutaj tylko zaznaczamy kategorię.
 */
const KATEGORIA_PRZYPIECIA = { panel: 'panels', inwerter: 'inverter', magazyn: 'battery' };
export const przypiecieZAdresu = (search) => {
  const m = String(new URLSearchParams(search).get('przypnij') || '').match(/^(panel|inwerter|magazyn)-(\d{1,9})$/);
  return m ? { kategoria: m[1], sekcja: KATEGORIA_PRZYPIECIA[m[1]], modelId: Number(m[2]) } : null;
};

const stateFromSearch = (search) => {
  const q = new URLSearchParams(search);
  if (![...q.keys()].length) return initialState;

  const next = { ...initialState };
  const przypiete = przypiecieZAdresu(search);
  if (przypiete) next.selected = { ...next.selected, [przypiete.sekcja]: true };

  const czego = (q.get('czego') || '').split(',').map((s) => s.trim()).filter(Boolean);
  const znane = czego.filter((k) => ['panels', 'inverter', 'battery'].includes(k));
  if (znane.length) {
    next.selected = {
      panels: znane.includes('panels'),
      inverter: znane.includes('inverter'),
      battery: znane.includes('battery'),
    };
  }

  const budzet = q.get('budzet');
  if (budzet === 'dowolny') {
    next.budget_any = true;
  } else if (budzet) {
    const kwota = Number(budzet);
    if (Number.isFinite(kwota) && kwota > 0) {
      next.budget_total_pln = kwota;
      next.budget_any = false;
    }
  }

  const kwp = Number(q.get('kwp'));
  if (Number.isFinite(kwp) && kwp > 0 && kwp <= 100) {
    next.panels = { ...next.panels, target_power_kwp: String(kwp) };
  }

  if (Number.isFinite(kwp) && kwp > 0 && kwp <= 100) {
    const zakres = zakresFalownika(kwp);
    next.inverter = {
      ...next.inverter,
      power_kw_min: String(zakres.min),
      power_kw_max: String(zakres.max),
      power_auto: true,
    };
  }

  // Pojemność magazynu policzona w kalkulatorze mocy. Wchodzi jako DOLNA granica:
  // „co najmniej tyle" to sens rekomendacji, a górnej i tak nie znamy.
  const kwh = Number(q.get('kwh'));
  if (Number.isFinite(kwh) && kwh > 0 && kwh <= 200) {
    next.battery = { ...next.battery, capacity_kwh_min: String(kwh) };
  }

  return next;
};

/**
 * Etap, od którego otwiera się kreator.
 *
 * Wejście ze strony głównej niesie już kategorie, budżet i moc (`?czego=&kwp=&budzet=`),
 * czyli etapy „Kategorie" i „Budżet i moc" są WYPEŁNIONE, zanim strona się pokaże.
 * Zaczynanie od pierwszego z nich kazałoby przewijać przez odpowiedzi, które
 * użytkownik podał chwilę wcześniej — i wyglądałoby, jakby formularz na stronie
 * głównej niczego nie zrobił.
 *
 * Bez mocy zostajemy na etapie budżetu: kWp jest tam wymagane i bez niego kreator
 * i tak nie ruszy dalej.
 */
const etapZAdresu = (search) => {
  const q = new URLSearchParams(search);
  const czego = (q.get('czego') || '').split(',').map((x) => x.trim()).filter(Boolean);
  const znane = czego.filter((k) => ['panels', 'inverter', 'battery'].includes(k));
  if (!znane.length) return 'cats';

  const kwp = Number(q.get('kwp'));
  const maMoc = Number.isFinite(kwp) && kwp > 0 && kwp <= 100;
  if (znane.includes('panels') && !maMoc) return 'budget';

  return znane.find((k) => ['panels', 'inverter', 'battery'].includes(k)) || 'cats';
};

const KitAssistant = () => {
  const { t } = useTranslation('asystent');
  // Wartości z bazy (rodzaj, fazy, napięcie) zostają w zapytaniach bez zmian,
  // tłumaczymy tylko to, co widać.
  const wart = (v) => (v ? t(`wartosci.${v}`, { defaultValue: v }) : v);
  useDocumentTitle(t('kreator.tytul'));
  // Leniwa inicjalizacja — wykonuje się RAZ, przy pierwszym renderze. Późniejsze
  // zmiany adresu nie mają nadpisywać tego, co użytkownik już wyklikał.
  /**
   * Sesja z poprzedniego wejscia — patrz lib/sesjaAsystenta.
   *
   * Odtwarzamy ja, gdy adres jest TEN SAM co przy zapisie (powrot „wstecz" albo
   * odswiezenie) albo gdy adres jest pusty. Inny adres = swiadome wejscie z nowymi
   * parametrami, np. skrotem ze strony glownej — wtedy wygrywa adres.
   */
  const sesja = useRef((() => {
    const zapisana = odczytajSesjeAsystenta();
    if (!zapisana) return null;
    const tenSamAdres = (zapisana.adres || '') === window.location.search;
    return tenSamAdres || !window.location.search ? zapisana : null;
  })()).current;
  const [state, dispatch] = useReducer(
    reducer, window.location.search,
    (search) => (sesja?.stan ? sesja.stan : stateFromSearch(search)),
  );
  // Domyślka na wypadek stanu zapisanego PRZED wprowadzeniem trybów (sesja
  // z pamięci albo zestaw z linku) — brak pola znaczy „moc w kWp", czyli tak,
  // jak asystent działał do tej pory.
  const trybMocy = state.panels.tryb_mocy || 'kwp';

  // Przypięcie z adresu (`przypnij=…`): pobieramy najtańszą ofertę modelu i ustawiamy ją jak przypięcie
  // z dymka propozycji. Raz na wejście; gdy model nie ma oferty z ceną, zostaje zwykły dobór.
  useEffect(() => {
    const p = przypiecieZAdresu(window.location.search);
    if (!p) return undefined;
    let anulowane = false;
    przypietyZModelu(p.kategoria, p.modelId).then((payload) => {
      if (!anulowane && payload) dispatch({ type: 'SET_PINNED', category: p.sekcja, payload });
    });
    return () => { anulowane = true; };
  }, []);

  const [result, setResult] = useState(sesja?.wynik || null);
  // Payload ostatniego doboru. Backend NIE odsyla zapytania w odpowiedzi, wiec
  // `result.request` bylo zawsze puste — a bez niego nie da sie ani zapisac
  // zestawu z parametrami, ani poprosic o zamiennik pozycji.
  const [ostatniPayload, setOstatniPayload] = useState(sesja?.payload || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Nazwa zestawu — wpisywana NA STARCIE, nie dopiero przy zapisie. Kto dobiera
  // kilka zestawów pod różnych klientów, po trzecim nie pamięta, który był czyj;
  // wpisanie nazwiska na wejściu kosztuje sekundę i rozstrzyga to od razu.
  // Nie jest parametrem doboru — patrz komentarz przy `zapiszNazweZestawu`.
  const [nazwaZestawu, setNazwaZestawu] = useState(() => odczytajNazweZestawu());
  useEffect(() => { zapiszNazweZestawu(nazwaZestawu); }, [nazwaZestawu]);

  /**
   * Tryb „dla klienta” (`?klient=<id>`, wejście z karty klienta, 2026-10-11) — patrz lib/klienci/asystentKlienta.
   *
   * Źródłem trybu jest adres, nie stan: przeżywa odświeżenie i przejścia między etapami, a usunięcie parametru
   * („Dobieraj bez klienta”) wraca do zwykłego asystenta bez utraty formularza. Klienci są tylko w wersji
   * polskiej. Gdy karty nie da się wczytać (brak logowania, brak dostępu), asystent działa zwyczajnie,
   * z jednym zdaniem informacji zamiast błędu.
   */
  const location = useLocation();
  const navigate = useNavigate();
  const klientId = jestPolska ? klientZAdresu(location.search) : null;
  const [klient, setKlient] = useState(null); // { id, nazwa, kwp, kwh }
  const [bladKlienta, setBladKlienta] = useState(false);
  const uzupelnionyDla = useRef(null);
  useEffect(() => {
    if (!klientId) { setKlient(null); setBladKlienta(false); return undefined; }
    let anulowane = false;
    setBladKlienta(false);
    getKlient(klientId).then((res) => {
      if (anulowane) return;
      if (!res.ok) { setKlient(null); setBladKlienta(true); return; }
      const w = wartosciZKlienta(res.data);
      setKlient({ id: klientId, ...w });
      // Raz na wejście i NIE po odtworzeniu sesji: odświeżona strona ma pokazać to, co użytkownik zostawił
      // (także pole, które sam wyczyścił), a nie wartości z karty od nowa. Nazwa zestawu z poprzedniej sesji
      // należała do kogoś innego, więc przy świeżym wejściu bierzemy nazwę klienta.
      if (uzupelnionyDla.current === klientId) return;
      uzupelnionyDla.current = klientId;
      if (!sesja?.stan) {
        dispatch({ type: 'PREFILL_KLIENT', wartosci: w });
        setNazwaZestawu(w.nazwa);
      }
    });
    return () => { anulowane = true; };
  }, [klientId, sesja]);
  const bezKlienta = () => navigate(adresBezKlienta(location.search), { replace: true });

  /**
   * Preferencje z konta (PLAN_SEGMENTY §5) — dołączane do żądania doboru.
   * Trzymamy w REF, nie w state: zmiana preferencji nie ma przerysowywać
   * formularza, ma tylko jechać z następnym żądaniem. Gość = null = brak pola.
   */
  const { isAuthenticated, firmowe } = useAuth();
  // Pole „Nazwa zestawu” (przypisanie do klienta) widzi tylko firma i tryb klienta, patrz komentarz przy polu.
  const pokazNazwe = Boolean(firmowe || klientId);
  // Bez konta (FE-08, decyzja właściciela 2026-10-19): jeden dobór w tej przeglądarce i widoczny najtańszy
  // zestaw. Użycie zapisujemy po wyniku z zestawami (błąd i „brak wyników” nie zabierają doboru), a wyniki
  // odtworzone z sesji karty nie liczą się drugi raz. Ref, żeby `runSuggest` nie zmieniał się z każdym renderem.
  const limitDoboru = useLimitBezKonta('asystent');
  const { bezKonta } = limitDoboru;
  const limitDoboruRef = useRef(limitDoboru);
  limitDoboruRef.current = limitDoboru;
  const [oknoKonta, setOknoKonta] = useState(null); // 'limit' | 'zestawy'
  const preferencjeKonta = useRef(null);
  // Moduły z ustawień konta W STANIE, nie w refie: w trybie „tylko wybrane
  // moduły" sterują tym, CO SIĘ RYSUJE w sekcji modułów, więc ich wczytanie
  // musi przerysować formularz.
  const [modulyKonta, setModulyKonta] = useState(null);
  // „Dobierz inne” przy „tylko wybrane moduły” (2026-10-30): tylko w tym doborze, ustawienia konta bez zmian.
  const [dobierzInneModuly, setDobierzInneModuly] = useState(false);
  const tylkoWybraneModuly = modulyKonta?.tryb === 'slots' && !dobierzInneModuly;
  const przelaczInneModuly = (inne) => {
    setDobierzInneModuly(inne);
    // Przypięty moduł pochodzi z poprzedniego widoku: po przełączeniu nie może po cichu wejść do zestawu.
    dispatch({ type: 'SET_PINNED', category: 'panels', payload: null });
  };
  // Wczytanie preferencji jest ASYNCHRONICZNE, a w trybie „tylko wybrane moduły"
  // dochodzi do niego rozwiązanie slotów (osobne zapytania do katalogu).
  // Trzymamy obietnicę i czekamy na nią przed doborem — inaczej kliknięcie
  // „Dobierz" tuż po wejściu na stronę poleciałoby bez ustawień konta, czyli
  // po cichu zignorowało twardy filtr, który użytkownik świadomie włączył.
  const preferencjeGotowe = useRef(null);
  useEffect(() => {
    if (!isAuthenticated) {
      preferencjeKonta.current = null;
      preferencjeGotowe.current = null;
      return undefined;
    }
    let cancelled = false;
    preferencjeGotowe.current = (async () => {
      const res = await getKitPreferences();
      if (cancelled || !res.ok) return;
      const p = res.data;
      const tryb = p.module_scope || 'any';
      // Serie i modele wskazane obok marek (KitPreferences.wybrane) liczą się jak marki.
      const wybrane = p.wybrane || {};
      const serieKat = (kat) => (wybrane[kat]?.serie || []).map((s) => `${s.producent}|${s.seria}`);
      const modeleKat = (kat) => (wybrane[kat]?.modele || []).map((m) => m.id);
      const maWybrane = ['inverter', 'battery', 'panels'].some((k) => serieKat(k).length || modeleKat(k).length);
      const maMarki = (p.inverter_manufacturers?.length || p.battery_manufacturers?.length
                       || p.module_manufacturers?.length || maWybrane);
      const maSklepy = (p.sellers || []).length > 0;
      const maSloty = (p.module_slots || []).some((s) => s && !slotPusty(s));
      // Sam tryb modułów też jest preferencją — bez tego warunku zaznaczenie
      // „tylko wybrane moduły" przy pustych listach marek nie dojechałoby nigdzie.
      // Sklepy i sloty liczą się tak samo: bez nich wariant „Preferowane wybory"
      // nie miałby z czego powstać u kogoś, kto wskazał tylko hurtownie.
      if (!maMarki && !maSklepy && !maSloty && tryb === 'any') {
        preferencjeKonta.current = null;
        setModulyKonta(null);
        return;
      }

      // Sloty rozwiązujemy ZAWSZE, gdy są wypełnione — nie tylko w trybie
      // twardym. Przy odznaczonym „tylko wybrane moduły" te same modele idą
      // jako MIĘKKA preferencja i budują osobny wariant zestawu.
      let ofertyModulow = [];
      if (maSloty) {
        // Sklepy zawężają dopiero w trybie „tylko moje sklepy" — tak samo jak
        // w podglądzie slotów w ustawieniach konta.
        const sklepy = p.seller_scope === 'only' ? (p.sellers || []) : [];
        ofertyModulow = await rozwiazSlotyPelne(
          p.module_slots, p.module_manufacturers || [], sklepy, p.module_min_stock || 0,
        );
      }
      if (cancelled) return;
      const idModulow = ofertyModulow.map((o) => o.model_id);
      preferencjeKonta.current = {
        inverter_manufacturers: p.inverter_manufacturers || [],
        battery_manufacturers: p.battery_manufacturers || [],
        module_manufacturers: p.module_manufacturers || [],
        manufacturers_only: Boolean(p.manufacturers_only),
        module_scope: tryb,
        // Ten sam zbiór modeli w dwóch różnych rolach, rozdzielony świadomie:
        // `module_model_ids` ODSIEWA (tylko w trybie twardym), a
        // `preferred_module_ids` tylko PODPOWIADA (poza nim).
        module_model_ids: tryb === 'slots' ? idModulow : [],
        preferred_module_ids: tryb === 'slots' ? [] : idModulow,
        sellers: p.sellers || [],
        inverter_series: serieKat('inverter'),
        battery_series: serieKat('battery'),
        module_series: serieKat('panels'),
        inverter_model_ids: modeleKat('inverter'),
        battery_model_ids: modeleKat('battery'),
        panel_model_ids: modeleKat('panels'),
      };
      // Do interfejsu, nie do żądania: w trybie twardym sekcja modułów pokazuje
      // te oferty zamiast pól mocy i filtrów.
      setModulyKonta({ tryb, oferty: ofertyModulow });
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const resultsRef = useRef(null);
  const formTopRef = useRef(null);
  const budgetRef = useRef(null);
  const panelsRef = useRef(null);
  const inverterRef = useRef(null);
  const batteryRef = useRef(null);
  const konstrukcjaRef = useRef(null);
  const sectionRef = { panels: panelsRef, inverter: inverterRef, battery: batteryRef };

  const selectedCats = Object.keys(state.selected).filter((k) => state.selected[k]);
  // Moc instalacji (kWp) jest WYMAGANA, gdy wybrano moduły PV — bez niej silnik nie policzy
  // liczby modułów (backend zwróci 400). Blokujemy CTA, żeby nie wysyłać niekompletnego żądania.
  const kwpOk = !state.selected.panels || numOrNull(state.panels.target_power_kwp) > 0;
  const budgetOk = state.budget_any || numOrNull(state.budget_total_pln) > 0;
  const canSubmit = budgetOk && selectedCats.length > 0 && kwpOk;

  // --- KREATOR ETAPÓW: aktywny etap „wypchnięty", auto-przejście po uzupełnieniu ---
  const [konstrukcja, setKonstrukcja] = useState(() => odczytajDaneProjektu().konstrukcja);
  const [montazWliczony, setMontazWliczony] = useState(() => odczytajDaneProjektu().montazWliczony);
  const [activeStage, setActiveStage] = useState(() => etapZAdresu(window.location.search));

  // Powrót z Projektu 3D (`?z3d=1`): raz wypełniamy formularz danymi modelu, zdejmujemy parametr z adresu
  // (odświeżenie nie nadpisze już zmian) i zostawiamy pasek z tym, co przyszło, i powrotem do modelu.
  const [zModelu, setZModelu] = useState(null);
  useEffect(() => {
    if (!wczytacZ3D(location.search)) return;
    const d = odczytajZProjektu3D();
    if (d) {
      dispatch({ type: 'PREFILL_3D', dane: d });
      const czesc = {};
      if (d.konstrukcja) { czesc.konstrukcja = d.konstrukcja; setKonstrukcja(d.konstrukcja); }
      if (d.wojewodztwo) czesc.wojewodztwo = d.wojewodztwo;
      // Tryb klienta nie zapisuje odpowiedzi w sesji przeglądarki: przeszłyby do oferty następnego klienta
      // (dane klienta trzyma jego karta, `dane.energia`).
      if (!klientId && Object.keys(czesc).length) zapiszDaneProjektu(czesc);
      setZModelu(d);
    }
    navigate(adresBezZ3D(location.pathname, location.search), { replace: true });
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rozbicie budżetu na „nasze" pozycje i to, co zostaje dla silnika.
  const kosztyDodatkoweTeraz = kosztyWlasne(state, konstrukcja, montazWliczony);
  const naSprzetTeraz = Math.max(0, (numOrNull(state.budget_total_pln) || 0) - kosztyDodatkoweTeraz);

  const advanceTimer = useRef(null);
  const stageDone = {
    cats: selectedCats.length > 0,
    budget: budgetOk && kwpOk,
    panels: numOrNull(state.panels.target_power_kwp) > 0,
    // Przypiety produkt z dymka TEZ zalicza etap: skoro uzytkownik wskazal konkretny
    // model, to odpowiedzial na pytanie tej sekcji mocniej niz jakimkolwiek zakresem.
    // Bez tego punkt zostawal szary mimo zaznaczonego „wybierz do zestawu".
    inverter: numOrNull(state.inverter.power_kw_min) > 0 || numOrNull(state.inverter.power_kw_max) > 0
      || !!state.pinned.inverter,
    battery: numOrNull(state.battery.capacity_kwh_min) > 0 || numOrNull(state.battery.capacity_kwh_max) > 0
      || !!state.pinned.battery,
    // Konstrukcja ma sensowna wartosc domyslna (blacha trapezowa), wiec etap jest
    // zaliczony od razu — nikogo nie blokujemy pytaniem o dach.
    konstrukcja: true,
  };
  // Konstrukcja NA KONCU i tylko przy modulach: bez modulow nie ma czego mocowac,
  // a pytanie o pokrycie dachu przy samym falowniku byloby bez sensu.
  const stageOrder = ['cats', 'budget', ...['panels', 'inverter', 'battery'].filter((k) => state.selected[k]),
    ...(state.selected.panels ? ['konstrukcja'] : [])];
  const activateStage = useCallback((id) => {
    if (advanceTimer.current) { clearTimeout(advanceTimer.current); advanceTimer.current = null; }
    setActiveStage(id);
  }, []);
  const ctaRef = useRef(null);
  // „Dalej" — przejście do KOLEJNEGO etapu bez wymogu wypełnienia (defaulty są OK);
  // z ostatniego etapu prowadzi do ograniczeń/CTA.
  const goNext = (id) => {
    if (advanceTimer.current) { clearTimeout(advanceTimer.current); advanceTimer.current = null; }
    const idx = stageOrder.indexOf(id);
    const next = stageOrder[idx + 1];
    if (next) {
      setActiveStage(next);
      stageScrollRef[next]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setActiveStage(null);
      ctaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  /**
   * Warunek AUTO-przejścia, osobny od `stageDone`.
   *
   * Etapy z parą pól (min–max) uciekały spod kursora: wpisanie samej minimalnej mocy
   * falownika zaliczało etap i po chwili przerzucało na kolejny, zanim użytkownik
   * zdążył wpisać maksymalną. Tutaj wymagamy OBU wartości; kto chce podać tylko dolną
   * granicę, przechodzi dalej przyciskiem „Dalej ↓".
   */
  const stageAutoAdvance = {
    ...stageDone,
    inverter: numOrNull(state.inverter.power_kw_min) > 0 && numOrNull(state.inverter.power_kw_max) > 0,
    battery: numOrNull(state.battery.capacity_kwh_min) > 0 && numOrNull(state.battery.capacity_kwh_max) > 0,
  };
  const prevDoneRef = useRef(stageAutoAdvance);
  const stageScrollRef = { cats: formTopRef, budget: budgetRef, panels: panelsRef,
    inverter: inverterRef, battery: batteryRef, konstrukcja: konstrukcjaRef };
  useEffect(() => {
    const prev = prevDoneRef.current;
    prevDoneRef.current = stageAutoAdvance;
    // Auto-przejście TYLKO gdy aktywny etap właśnie przeszedł z „niedokończony" na „done".
    if (!(stageAutoAdvance[activeStage] && !prev[activeStage])) return;
    const idx = stageOrder.indexOf(activeStage);
    // KOLEJNY etap po kolei — NIE „pierwszy niedokończony". Moc instalacji (kWp) z sekcji
    // budżetu zalicza jednocześnie etap „Moduły PV", więc szukanie niedokończonego
    // przeskakiwało moduły o jeden etap za daleko.
    const next = stageOrder[idx + 1];
    if (!next) return;
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      setActiveStage(next);
      stageScrollRef[next]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 700);
  });

  // --- PROPONOWANE PRODUKTY (dymki upsell przy sekcjach) ---
  const [reco, setReco] = useState({ panels: null, inverter: null, battery: null });
  const [recoLoading, setRecoLoading] = useState({ panels: true, inverter: true, battery: true });
  const invType = state.inverter.filters.types_in[0] || null;
  const invMinV = numOrNull(state.inverter.power_kw_min);
  const invMaxV = numOrNull(state.inverter.power_kw_max);
  const capMinV = numOrNull(state.battery.capacity_kwh_min);
  const capMaxV = numOrNull(state.battery.capacity_kwh_max);
  const reqAvail = state.constraints.require_available;

  useEffect(() => {
    let cancelled = false;
    recommendPanel()
      .then((p) => { if (!cancelled) setReco((r) => ({ ...r, panels: p })); })
      .finally(() => { if (!cancelled) setRecoLoading((l) => ({ ...l, panels: false })); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Przypięty produkt „mrozi" dymek — nie nadpisujemy wyboru użytkownika refetchem.
    if (!state.selected.inverter || state.pinned.inverter) return undefined;
    let cancelled = false;
    setRecoLoading((l) => ({ ...l, inverter: true }));
    const t = setTimeout(() => {
      recommendInverter({ type: invType, minPower: invMinV, maxPower: invMaxV, availableOnly: reqAvail })
        .then((p) => { if (!cancelled) setReco((r) => ({ ...r, inverter: p })); })
        .finally(() => { if (!cancelled) setRecoLoading((l) => ({ ...l, inverter: false })); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [state.selected.inverter, state.pinned.inverter, invType, invMinV, invMaxV, reqAvail]);

  useEffect(() => {
    if (!state.selected.battery || state.pinned.battery) return undefined;
    let cancelled = false;
    setRecoLoading((l) => ({ ...l, battery: true }));
    const t = setTimeout(() => {
      // Ten sam zapas ±0,5 kWh co w payloadzie silnika (5 ≈ 5,12 kWh).
      recommendBattery({
        minCap: capMinV != null ? Math.max(0, +(capMinV - 0.5).toFixed(2)) : null,
        maxCap: capMaxV != null ? +(capMaxV + 0.5).toFixed(2) : null,
        availableOnly: reqAvail,
      })
        .then((p) => { if (!cancelled) setReco((r) => ({ ...r, battery: p })); })
        .finally(() => { if (!cancelled) setRecoLoading((l) => ({ ...l, battery: false })); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [state.selected.battery, state.pinned.battery, capMinV, capMaxV, reqAvail]);

  const setPinned = (category, payload) => (checked) =>
    dispatch({ type: 'SET_PINNED', category, payload: checked ? payload : null });

  const recoBubble = (key) => {
    if (key === 'panels') {
      // W trybie „tylko wybrane moduły" dymek POKAZUJE NASZĄ PROPOZYCJĘ i nic
      // więcej: wyboru dokonuje się na kafelkach obok, a podmienianie dymka na
      // to, co użytkownik przed chwilą kliknął, kasowałoby jedyne miejsce,
      // w którym widać alternatywę dla własnej listy. Przypinanie też stąd
      // znika — dwa sposoby na tę samą rzecz obok siebie tylko mylą.
      const wybraneModuly = tylkoWybraneModuly;
      const p = wybraneModuly ? reco.panels : (state.pinned.panels || reco.panels);
      return (
        <RecommendedProduct title={t('kreator.propozycja.modul')} loading={recoLoading.panels && !state.pinned.panels} product={p}
          link={`/panel/${p?.id}`}
          specs={[
            { label: t('kreator.propozycja.moc'), value: p?.power ? `${p.power} W` : null },
            { label: t('kreator.propozycja.rodzaj'), value: [p?.type, p?.type_secondary].filter(Boolean).map(wart).join(', ') || null },
            { label: t('kreator.propozycja.sprawnosc'), value: p?.efficiency ? `${p.efficiency}%` : null },
          ]}
          pinnable={!wybraneModuly && !!p?.model_id}
          pinned={!wybraneModuly && !!state.pinned.panels}
          onPin={setPinned('panels', p)} />
      );
    }
    if (key === 'inverter') {
      const p = state.pinned.inverter || reco.inverter;
      return (
        <RecommendedProduct title={t('kreator.propozycja.falownik')} loading={recoLoading.inverter && !state.pinned.inverter} product={p}
          link={`/inwerter/${p?.id}`}
          specs={[
            { label: t('kreator.propozycja.moc'), value: p?.power ? `${p.power} kW` : null },
            { label: t('kreator.propozycja.rodzaj'), value: wart(p?.type) },
            { label: t('kreator.propozycja.fazy'), value: wart(p?.phase) },
          ]}
          pinnable={!!p?.model_id} pinned={!!state.pinned.inverter} onPin={setPinned('inverter', p)} />
      );
    }
    const b = state.pinned.battery || reco.battery;
    const mag = b?.magazine;
    return (
      <RecommendedProduct title={t('kreator.propozycja.magazyn')} loading={recoLoading.battery && !state.pinned.battery} product={mag}
        link={`/magazyn/${mag?.id}`}
        specs={[
          { label: t('kreator.propozycja.pojemnosc'), value: mag?.capacity ? `${mag.capacity} kWh` : null },
          { label: t('kreator.propozycja.napiecie'), value: wart(mag?.voltage) },
        ]}
        bmsLine={b?.bms ? `+ BMS (${b.bms.manufacturer}): ${b.bms.name}${b.bms.price != null ? `, ${pln(b.bms.price)}` : ''}` : null}
        bmsSpecLine={liniaParametrowBms(b?.bms?.electrical_spec, t)}
        note={b?.bmsMissing ? t('kreator.propozycja.brak_bms') : null}
        pinnable={!!mag?.model_id} pinned={!!state.pinned.battery} onPin={setPinned('battery', b)} />
    );
  };

  const variantsCount = result?.variants?.length || 0;
  const scrollTo = (ref, block = 'center') => () => ref.current?.scrollIntoView({ behavior: 'smooth', block });
  // Interaktywna linia postępu (prawa krawędź). Przy każdej kategorii pokazujemy WYMAGANE
  // wpisane wartości (np. „45 tys., 8 kWp"), a krok z gwiazdką = wymagany input. Punkt „done"
  // dopiero po wpisaniu wartości — dzięki `detail` widać, DLACZEGO krok jest zaliczony
  // (wcześniej „Moduły PV" zapalało się od pola moc w sekcji budżetu, co myliło).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const progressSteps = useMemo(() => {
    const budgetVal = numOrNull(state.budget_total_pln);
    const kwpVal = numOrNull(state.panels.target_power_kwp);
    const invMin = numOrNull(state.inverter.power_kw_min);
    const invMax = numOrNull(state.inverter.power_kw_max);
    const capMin = numOrNull(state.battery.capacity_kwh_min);
    const capMax = numOrNull(state.battery.capacity_kwh_max);
    const kZl = (v) => (v >= 1000 ? t('kreator.postep.tysiace', { n: Math.round(v / 1000) }) : `${v}`);
    const range = (lo, hi, u) => (lo && hi ? `${lo}–${hi} ${u}`
      : lo ? t('kreator.postep.od', { lo, u }) : hi ? t('kreator.postep.do', { hi, u }) : null);
    const budgetLabel = state.budget_any ? t('kreator.budzet.dowolny') : (budgetVal > 0 ? kZl(budgetVal) : null);
    const budgetDetail = budgetLabel
      ? [budgetLabel, state.selected.panels && kwpVal > 0 ? `${kwpVal} kWp` : null].filter(Boolean).join(', ')
      : null;

    const raw = [
      { id: 'cats', label: t('kreator.postep.kategorie'), icon: faLayerGroup, required: false, detail: null, done: selectedCats.length > 0, onClick: scrollTo(formTopRef, 'start') },
      { id: 'budget', label: t('kreator.postep.budzet_i_moc'), icon: faWallet, required: true, detail: budgetDetail,
        done: (state.budget_any || budgetVal > 0) && (!state.selected.panels || kwpVal > 0), onClick: scrollTo(budgetRef) },
    ];
    // Przypiety produkt zalicza etap i WYPIERA zakres w opisie: skoro wybrano konkretny
    // model, zakres mocy przestaje byc tym, co uzytkownik chce tu zobaczyc.
    const przypiety = (kat) => (state.pinned[kat] ? t('kreator.postep.wybrany_produkt') : null);
    // W trybie „liczba modułów" pokazujemy JEDNO i DRUGIE: użytkownik wpisał
    // sztuki, ale to kWp decyduje o doborze — niech widzi, co z jego liczby wyszło.
    const sztukiVal = numOrNull(state.panels.module_count);
    const opisMocy = trybMocy === 'sztuki' && sztukiVal > 0 && kwpVal > 0
      ? t('kreator.postep.sztuki_kwp', { sztuki: sztukiVal, kwp: kwpVal })
      : (kwpVal > 0 ? `${kwpVal} kWp` : null);
    if (state.selected.panels) raw.push({ id: 'panels', label: t('kreator.postep.moduly'), icon: faSolarPanel, required: true, detail: przypiety('panels') || opisMocy, done: kwpVal > 0, onClick: scrollTo(panelsRef) });
    if (state.selected.inverter) raw.push({ id: 'inverter', label: t('kreator.postep.falownik'), icon: faBolt, required: false, detail: przypiety('inverter') || range(invMin, invMax, 'kW'), done: invMin > 0 || invMax > 0 || !!state.pinned.inverter, onClick: scrollTo(inverterRef) });
    if (state.selected.battery) raw.push({ id: 'battery', label: t('kreator.postep.magazyn'), icon: faBatteryFull, required: false, detail: przypiety('battery') || range(capMin, capMax, 'kWh'), done: capMin > 0 || capMax > 0 || !!state.pinned.battery, onClick: scrollTo(batteryRef) });
    if (state.selected.panels) raw.push({ id: 'konstrukcja', label: t('kreator.postep.konstrukcja'), icon: faScrewdriverWrench,
      required: false, detail: t(`konstrukcja.rodzaj.${konstrukcjaWg(konstrukcja).key}`, { defaultValue: konstrukcjaWg(konstrukcja).label }), done: true, onClick: scrollTo(konstrukcjaRef) });
    raw.push({ id: 'results', label: t('kreator.postep.wyniki'), icon: faListCheck, required: false, detail: null, done: variantsCount > 0, onClick: scrollTo(resultsRef, 'start') });
    let currentSet = false;
    return raw.map((r) => {
      let status;
      if (r.done) status = 'done';
      else if ((r.required || r.id === 'results') && !currentSet) { status = 'current'; currentSet = true; }
      else status = 'todo';
      return { id: r.id, label: r.label, icon: r.icon, required: r.required, detail: r.detail, status, onClick: r.onClick };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.budget_total_pln, state.budget_any, state.panels.target_power_kwp, state.inverter.power_kw_min,
    trybMocy, state.panels.module_count,
    state.inverter.power_kw_max, state.battery.capacity_kwh_min, state.battery.capacity_kwh_max,
    state.selected.panels, state.selected.inverter, state.selected.battery, selectedCats.length, variantsCount,
    konstrukcja, state.pinned.panels, state.pinned.inverter, state.pinned.battery, t]);

  const runSuggest = useCallback(async (explicitState) => {
    if (limitDoboruRef.current.wyczerpany) { setOknoKonta('limit'); return; }
    const payload = buildPayload(explicitState, kosztyWlasne(explicitState, konstrukcja, montazWliczony));
    setLoading(true);
    setError(null);
    // Preferencje konta muszą być gotowe ZANIM złożymy żądanie — patrz komentarz
    // przy `preferencjeGotowe`. `catch` bez obsługi: awaria wczytywania preferencji
    // nie może zablokować doboru, w najgorszym razie zestaw powstanie bez nich.
    if (preferencjeGotowe.current) {
      try { await preferencjeGotowe.current; } catch { /* dobieramy bez preferencji */ }
    }
    if (preferencjeKonta.current) {
      // „Dobierz inne”: te same modele przechodzą z roli odsiewającej do podpowiedzi, jak poza trybem twardym.
      const p = preferencjeKonta.current;
      payload.preferences = dobierzInneModuly && p.module_scope === 'slots'
        ? { ...p, module_scope: 'any', module_model_ids: [], preferred_module_ids: p.module_model_ids }
        : p;
    }
    setOstatniPayload(payload);
    try {
      const res = await suggestKits(payload);
      setResult(res);
      if (res?.variants?.length) limitDoboruRef.current.sprobuj();
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    } catch (e) {
      setError(e.message || t('kreator.blad'));
      setResult(null);
    } finally {
      setLoading(false);
    }
    // Zależności MUSZĄ obejmować konstrukcję i montaż: bez nich `kosztyWlasne`
    // liczyłoby się z wartości zamrożonych przy pierwszym renderze, więc zmiana
    // rodzaju połaci nie zmieniałaby budżetu wysyłanego do silnika.
  }, [konstrukcja, montazWliczony, t]);

  // Przewinięcie do etapu, na którym realnie zaczynamy. Bez tego strona otwiera się
  // na górze, a „wypchnięty" etap trzeba dopiero znaleźć — czyli skrót ze strony
  // głównej działałby tylko w stanie komponentu, nie dla patrzącego.
  const przewinietoNaStart = useRef(false);
  useEffect(() => {
    if (przewinietoNaStart.current) return;
    przewinietoNaStart.current = true;
    if (activeStage === 'cats' || !activeStage) return;
    const cel = stageScrollRef[activeStage]?.current;
    if (cel) setTimeout(() => cel.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zapis przy KAZDEJ zmianie formularza i wynikow — cofniecie moze nastapic
  // w dowolnym momencie, wiec nie ma jednego "dobrego" miejsca na zapis.
  // Adres w zależnościach: po wyjściu z trybu klienta sesja ma należeć do nowego adresu, inaczej powrót
  // „wstecz” na zwykły asystent nie odtworzyłby formularza (patrz `tenSamAdres` wyżej).
  useEffect(() => { zapiszSesjeAsystenta(state, result, ostatniPayload); },
    [state, result, ostatniPayload, location.search]);

  const onSubmit = () => { if (canSubmit) runSuggest(state); };

  // Powrót do ustawień domyślnych: formularz + wyniki + przypięte produkty + etap.
  const onReset = () => {
    wyczyscSesjeAsystenta();
    dispatch({ type: 'RESET' });
    // W trybie klienta „od nowa” znaczy od wartości z jego karty, a nie od pustego formularza.
    if (klient) dispatch({ type: 'PREFILL_KLIENT', wartosci: klient });
    setResult(null);
    setOstatniPayload(null);
    setError(null);
    setActiveStage('cats');
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onRelax = (relax) => {
    const next = applyRelaxation(state, relax);
    dispatch({ type: 'REPLACE', state: next });
    runSuggest(next);
  };
  const onRaiseBudget = (amount) => {
    // ustawienie konkretnej kwoty wyłącza tryb „dowolny"
    const next = { ...state, budget_total_pln: amount, budget_any: false };
    dispatch({ type: 'REPLACE', state: next });
    runSuggest(next);
  };

  const variants = result?.variants || [];
  const noResults = result?.no_results;
  // Bez konta widać najtańszy zestaw, reszta wariantów jest za darmowym kontem (FE-08).
  const { widoczne: wariantyWidoczne, ukryte: wariantyUkryte } = bezKonta
    ? podzielWarianty(variants) : { widoczne: variants, ukryte: [] };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 mt-8 mb-28 md:mb-16">
      <ProgressRail steps={progressSteps} />
      {/* W trybie klienta „Powrót” prowadzi do jego karty (runda 4, 2026-10-11). */}
      <div className="mb-4"><BackButton powrot={klientId ? adresKartyKlienta(klientId) : null} /></div>
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">{t('kreator.tytul')}</h1>
      <p className="text-sm text-gray-500 mb-6 max-w-2xl">
        {t('kreator.wstep')}
      </p>

      {/* Pasek trybu „dla klienta”: dla kogo dobieramy, co przyszło z karty i jak z trybu wyjść. Kolor niesie
          sam znacznik po lewej, bez osobnego kafelka z akapitem. */}
      {klient && (
        <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 border-l-4 border-l-blue-600 bg-white px-4 py-3">
          <p className="min-w-0 flex-1 text-sm text-gray-800">
            Dobierasz zestaw dla klienta: <strong className="text-gray-900">{klient.nazwa}</strong>
            {(klient.kwp || klient.kwh) && (
              <span className="block text-xs text-gray-500 mt-0.5">
                Z karty klienta: {[klient.kwp && `${klient.kwp.replace('.', ',')} kWp${klient.kwpZrodlo === 'zuzycie' && klient.zuzycieKwh ? ` (z zużycia ${klient.zuzycieKwh.toLocaleString('pl-PL')} kWh rocznie)` : ''}`, klient.kwh && `magazyn ${klient.kwh.replace('.', ',')} kWh`].filter(Boolean).join(', ')}
              </span>
            )}
          </p>
          <Link to={adresKartyKlienta(klient.id)} className="text-sm font-semibold text-blue-700 hover:underline">
            Karta klienta
          </Link>
          <button type="button" onClick={bezKlienta} className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline">
            Dobieraj bez klienta
          </button>
        </div>
      )}
      {bladKlienta && (
        <p className="mb-6 text-xs text-gray-500">
          Nie udało się wczytać karty klienta, więc asystent działa zwykłym trybem.{' '}
          <button type="button" onClick={bezKlienta} className="underline hover:text-gray-700">Ukryj</button>
        </p>
      )}

      {/* Nazwa NAD formularzem, a nie przy zapisie: to pierwsza rzecz, którą
          wie handlowiec siadający do wyceny („robię to dla Kowalskiego"),
          i ostatnia, o której pamięta po kwadransie klikania parametrów.
          Pole jest opcjonalne — przy pustym zapis nadal podstawi nazwę z daty.
          Tylko konto firmowe i tryb klienta (2026-10-23): osobie prywatnej „przypisz do klienta” nic nie mówi,
          a było pierwszym polem, które widziała po kliknięciu „Dobierz mój zestaw”. */}
      {pokazNazwe && (
      <div className="mb-6 max-w-md">
        <label htmlFor="kit-nazwa" className="block text-sm font-semibold text-gray-800 mb-1.5">
          {t('kreator.nazwa.etykieta')} <span className="font-normal text-gray-400">{t('kreator.nazwa.opcjonalnie')}</span>
        </label>
        <input
          id="kit-nazwa"
          type="text"
          value={nazwaZestawu}
          onChange={(e) => setNazwaZestawu(e.target.value)}
          maxLength={120}
          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          {t('kreator.nazwa.opis')}
        </p>
      </div>
      )}

      {/* Przełącznik drogi STOI TU, tuż nad oknem asystenta: to ostatni moment,
          w którym ktoś, kto nie zna mocy swojej instalacji, może przejść na
          pytania o dom, zamiast odbić się od pierwszego pola i wyjść. */}
      {/* W trybie klienta bez przełącznika: prosta droga kończy się szukaniem wykonawcy, a tu dobiera
          zestaw firma, która zna parametry instalacji. */}
      {!klient && <WyborDrogi aktywna="parametry" />}

      {zModelu && (
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-blue-200 bg-white px-4 py-3">
          <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-blue-600" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm text-gray-700">
            <span className="font-semibold text-gray-900">Z Projektu 3D{zModelu.miejsce ? `, ${zModelu.miejsce}` : ''}:</span>{' '}
            <span className="tabular-nums">
              {zModelu.liczbaModulow} × {zModelu.mocModuluW} W ({String(zModelu.kwp).replace('.', ',')} kWp)
              {zModelu.magazynKwh ? `, magazyn ${String(zModelu.magazynKwh).replace('.', ',')} kWh` : ''}
              {zModelu.rokKwh ? `, ok. ${zModelu.rokKwh.toLocaleString('pl-PL')} kWh rocznie` : ''}
            </span>
          </p>
          <Link to={ADRES_PROJEKTU_Z_ASYSTENTA} className="text-sm font-semibold text-blue-700 hover:underline">Wróć do modelu</Link>
        </div>
      )}
      {/* Preferowany sprzęt do teczki (P4): marki i full black, dobór do mocy z modelu. Nie w trybie klienta firmy. */}
      {zModelu && !klientId && <ZestawWedlugPreferencji zModelu={zModelu} zrodlo="projekt3d" />}

      <div ref={formTopRef} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-7">
        {/* 1. Kategorie */}
        <Stage id="cats" active={activeStage === 'cats'} onActivate={activateStage} onNext={goNext}>
        <div className="p-4 md:p-5">
          <label className="block text-sm font-semibold text-gray-900 mb-3">{t('kreator.krok_kategorie')}</label>
          <div className="grid sm:grid-cols-3 gap-3">
            {CATEGORY_CARDS.map((c) => {
              const on = state.selected[c.key];
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => dispatch({ type: 'TOGGLE_CATEGORY', key: c.key })}
                  className={`rounded-xl border-2 p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    on ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-semibold text-sm text-gray-900">
                      <FontAwesomeIcon icon={c.icon} className={on ? 'text-blue-600' : 'text-gray-400'} />
                      {t(`kreator.kategorie.${c.key}.etykieta`)}
                    </span>
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${on ? 'bg-blue-600 text-white' : 'bg-gray-100 text-transparent'}`}>
                      <FontAwesomeIcon icon={faCheck} />
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 mt-1 block">{t(`kreator.kategorie.${c.key}.podpowiedz`)}</span>
                </button>
              );
            })}
          </div>
        </div>
        </Stage>

        {/* 2. Budżet + moc + rodzaj instalacji */}
        <Stage id="budget" active={activeStage === 'budget'} onActivate={activateStage} onNext={goNext} className="mt-4">
        {/* SIATKA, nie `flex-wrap`: przy zawijaniu o szerokości kolumny decydował
            najdłuższy tekst pomocniczy w środku, więc dopisanie jednego zdania pod
            etykietą budżetu zrzucało „Orientacyjną moc" do drugiego rzędu — a to
            pole wymagane i ma być widoczne obok budżetu. Trzy równe kolumny trzymają
            układ niezależnie od długości podpisów; zawijają się one w kolumnie. */}
        <div ref={budgetRef} className="grid grid-cols-1 md:grid-cols-3 items-start gap-x-8 gap-y-4 p-4 md:p-5">
          <div>
            <span className="block text-sm font-semibold text-gray-900 mb-2">{t('kreator.rodzaj_instalacji.naglowek')}</span>
            <div role="group" aria-label={t('kreator.rodzaj_instalacji.aria')} className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              {[
                { v: '1F', l: t('kreator.rodzaj_instalacji.1F') },
                { v: null, l: t('kreator.rodzaj_instalacji.nie_wiem') },
                { v: '3F', l: t('kreator.rodzaj_instalacji.3F') },
              ].map((o, i) => {
                const active = (state.installation_phase ?? null) === o.v;
                return (
                  <button key={String(o.v)} type="button" onClick={() => dispatch({ type: 'SET_PHASE', value: o.v })}
                    aria-pressed={active}
                    className={`px-2.5 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${i > 0 ? 'border-l border-gray-200' : ''} ${active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {o.l}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('kreator.rodzaj_instalacji.opis')}</p>
          </div>
          <div>
            <label htmlFor="kit-budget" className="block text-sm font-semibold text-gray-900 mb-2">
              {t('kreator.budzet.etykieta')} {!state.budget_any && <span className="text-blue-500" title={t('kreator.pole_wymagane')}>*</span>}
              <span className="block text-[11px] font-normal text-gray-500 mt-0.5">
                {t('kreator.budzet.podpis')}
              </span>
            </label>
            <div className={`relative w-40 ${state.budget_any ? 'opacity-50' : ''}`}>
              <input id="kit-budget" type="number" inputMode="numeric" step="500" value={state.budget_total_pln}
                onChange={(e) => dispatch({ type: 'SET_BUDGET', value: e.target.value })}
                disabled={state.budget_any}
                className="w-full text-center tabular-nums border-2 border-blue-200 bg-blue-50/40 rounded-lg pl-3 pr-8 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-gray-50 disabled:border-gray-200" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">{KRAJ.symbol}</span>
            </div>
            <div className={`mt-3 w-40 ${state.budget_any ? 'opacity-40 pointer-events-none' : ''}`}>
              <BudgetSlider value={clampBudget(state.budget_total_pln)} min={BUDGET_MIN} max={BUDGET_MAX} step={1000}
                onChange={(v) => dispatch({ type: 'SET_BUDGET', value: v })} ariaLabel={t('kreator.budzet.suwak', { min: BUDGET_MIN / 1000, max: BUDGET_MAX / 1000, waluta: KRAJ.symbol })} />
            </div>
            {!state.budget_any && kosztyDodatkoweTeraz > 0 && (
              <p className="mt-2 text-[11px] text-gray-500 leading-4 w-56">
                {naSprzetTeraz > 0 ? (
                  <Trans t={t} i18nKey="kreator.budzet.rozbicie"
                    values={{ koszt: pln(kosztyDodatkoweTeraz), sprzet: pln(naSprzetTeraz) }}
                    components={{ b: <strong />, c: <strong className="text-gray-700" /> }} />
                ) : (
                  <span className="text-amber-700">
                    {t('kreator.budzet.za_niski', { koszt: pln(kosztyDodatkoweTeraz) })}
                  </span>
                )}
              </p>
            )}
            <label className="mt-2 flex items-center gap-2 cursor-pointer select-none w-fit">
              <input type="checkbox" checked={state.budget_any}
                onChange={(e) => dispatch({ type: 'SET_BUDGET_ANY', value: e.target.checked })}
                className="h-3.5 w-3.5 rounded accent-blue-600" />
              <span className="text-xs font-medium text-gray-700">{t('kreator.budzet.bez_limitu')}</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              {state.budget_any ? t('kreator.budzet.kazda_cena') : t('kreator.budzet.z_dostawa')}
            </p>
          </div>
          <div>
            <span className="block text-sm font-semibold text-gray-900 mb-2">
              {t('kreator.wielkosc.etykieta')} {state.selected.panels && <span className="text-blue-500" title={t('kreator.wielkosc.wymagane')}>*</span>}
            </span>

            {/* Dwie drogi do tej samej liczby. Ktoś zna moc, jaką chce mieć na
                dachu; ktoś inny wie, ile modułów mu się mieści — i dla niego
                przeliczanie w pamięci „ile to kWp" jest zbędną pracą. */}
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden mb-2">
              {[{ v: 'kwp', l: t('kreator.wielkosc.moc') }, { v: 'sztuki', l: t('kreator.wielkosc.liczba_modulow') }].map((o, i) => {
                const aktywny = trybMocy === o.v;
                return (
                  <button key={o.v} type="button" disabled={!state.selected.panels}
                    onClick={() => dispatch({ type: 'SET_TRYB_MOCY', value: o.v })}
                    aria-pressed={aktywny}
                    className={`px-2.5 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 ${
                      i > 0 ? 'border-l border-gray-200' : ''
                    } ${aktywny ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {o.l}
                  </button>
                );
              })}
            </div>

            {trybMocy === 'kwp' ? (
              <>
                <div className="relative w-28">
                  <input id="kit-kwp" type="number" inputMode="decimal" step="0.5" value={state.panels.target_power_kwp}
                    onChange={(e) => dispatch({ type: 'SET_KWP', value: e.target.value })}
                    disabled={!state.selected.panels}
                    aria-label={t('kreator.wielkosc.aria_kwp')}
                    aria-invalid={state.selected.panels && !(numOrNull(state.panels.target_power_kwp) > 0)}
                    className={`w-full text-center tabular-nums rounded-lg pl-3 pr-10 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200 ${
                      state.selected.panels ? 'border-2 border-blue-200 bg-blue-50/40' : 'border border-gray-200'
                    }`} />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">kWp</span>
                </div>
                {state.selected.panels && !(numOrNull(state.panels.target_power_kwp) > 0)
                  ? <p className="text-xs text-amber-600 mt-1">{t('kreator.wielkosc.podaj_moc')}</p>
                  : <p className="text-xs text-gray-500 mt-1">{t('kreator.wielkosc.wyliczymy')}</p>}
              </>
            ) : (
              <>
                {/* Tylko liczba sztuk. Moc modułu podaje się w sekcji „Moduły PV"
                    („Preferowana moc modułu") — to jedno i to samo pole stanu,
                    więc dublowanie go tutaj oznaczałoby dwa wejścia do tej samej
                    wartości w jednym formularzu. */}
                <div className="relative w-24">
                  <input id="kit-sztuki" type="number" inputMode="numeric" min="1" step="1"
                    value={state.panels.module_count}
                    onChange={(e) => dispatch({ type: 'SET_MODULE_COUNT', value: e.target.value })}
                    disabled={!state.selected.panels}
                    aria-label={t('kreator.wielkosc.liczba_modulow')}
                    aria-invalid={state.selected.panels && !(numOrNull(state.panels.module_count) > 0)}
                    className={`w-full text-center tabular-nums rounded-lg pl-3 pr-9 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200 ${
                      state.selected.panels ? 'border-2 border-blue-200 bg-blue-50/40' : 'border border-gray-200'
                    }`} />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">{t('kreator.wielkosc.szt')}</span>
                </div>
                {kwpZeSztuk(state.panels.module_count, state.panels.preferred_module_power_w)
                  ? (
                    <p className="text-xs text-gray-600 mt-1.5 w-56">
                      <Trans t={t} i18nKey="kreator.wielkosc.wynik_ze_sztuk"
                        values={{
                          sztuki: state.panels.module_count,
                          moc: state.panels.preferred_module_power_w,
                          kwp: numOrNull(state.panels.target_power_kwp),
                        }}
                        components={{ b: <strong className="text-gray-800 tabular-nums" /> }} />
                    </p>
                  ) : (
                    // Bez mocy modułu nie ma mocy instalacji, a ta decyduje
                    // o falowniku i o konstrukcji. Odsyłamy tam, gdzie się ją
                    // podaje, zamiast zostawiać użytkownika z samą informacją,
                    // że czegoś brakuje.
                    <p className="text-xs text-amber-600 mt-1.5 w-56 leading-4">
                      <Trans t={t} i18nKey="kreator.wielkosc.podaj_moc_modulu"
                        components={{
                          btn: <button type="button" onClick={scrollTo(sectionRef.panels)}
                            className="underline underline-offset-2 font-semibold hover:text-amber-700" />,
                        }} />
                    </p>
                  )}
              </>
            )}
          </div>
        </div>
        </Stage>

        {/* 3. Sekcje per zaznaczona kategoria (+ dymek z proponowanym produktem) */}
        <div className="space-y-4 mt-4">
          {CATEGORY_CARDS.filter((c) => state.selected[c.key]).map((c) => (
            <Stage key={c.key} id={c.key} active={activeStage === c.key} onActivate={activateStage} onNext={goNext}>
              <div ref={sectionRef[c.key]} className="grid lg:grid-cols-[minmax(0,1fr),250px] gap-3 items-start p-1.5">
                {/* Tryb „tylko wybrane moduły" ZASTĘPUJE sekcję modułów listą
                    z ustawień konta — patrz komentarz w WybraneModuly. */}
                {c.key === 'panels' && tylkoWybraneModuly ? (
                  <WybraneModuly
                    oferty={modulyKonta.oferty}
                    przypiety={state.pinned.panels}
                    onPrzypnij={(o) => dispatch({ type: 'SET_PINNED', category: 'panels', payload: o })}
                    onDobierzInne={() => przelaczInneModuly(true)}
                  />
                ) : c.key === 'panels' && dobierzInneModuly && modulyKonta?.tryb === 'slots' ? (
                  <div className="min-w-0 space-y-2">
                    <p className="text-xs text-gray-500 leading-5 px-1">
                      {t('wybrane_moduly.poza_lista')}{' '}
                      <button type="button" onClick={() => przelaczInneModuly(false)} className="font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">
                        {t('wybrane_moduly.wroc')}
                      </button>
                    </p>
                    <CategorySection category={c.key} state={state[c.key]} dispatch={dispatch} batterySelected={state.selected.battery} />
                  </div>
                ) : (
                  <CategorySection category={c.key} state={state[c.key]} dispatch={dispatch} batterySelected={state.selected.battery} />
                )}
                {recoBubble(c.key)}
              </div>
            </Stage>
          ))}

          {/* Konstrukcja montażowa — ostatni etap, tylko przy modułach PV.
              Wybór opisuje DACH, a nie zestaw, więc zapisuje się razem z resztą
              odpowiedzi o domu (lib/daneProjektu) i przechodzi dalej do VoltValue
              Projektu, zamiast ginąć przy przejściu do wyników. */}
          {state.selected.panels && (
            <Stage id="konstrukcja" active={activeStage === 'konstrukcja'}
                   onActivate={activateStage} onNext={goNext}>
              <div ref={konstrukcjaRef}>
                <KrokKonstrukcji
                  kwp={numOrNull(state.panels.target_power_kwp)}
                  mocModuluW={state.pinned.panels?.power || null}
                  wybrana={konstrukcja}
                  onZmiana={(v) => { setKonstrukcja(v); zapiszDaneProjektu({ konstrukcja: v }); }}
                />
              </div>
            </Stage>
          )}
        </div>

        {/* 4. Ograniczenia */}
        <div ref={ctaRef} className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-x-6 gap-y-3">
          {[
            { key: 'require_available', label: t('kreator.ograniczenia.dostepne') },
            { key: 'single_seller_only', label: t('kreator.ograniczenia.jeden_sprzedawca') },
            { key: 'allow_unknown_compatibility', label: t('kreator.ograniczenia.bez_kompatybilnosci') },
          ].map((c) => (
            <CheckBox key={c.key} checked={state.constraints[c.key]} label={c.label}
              onChange={(e) => dispatch({ type: 'SET_CONSTRAINT', key: c.key, value: e.target.checked })} />
          ))}
          {/* Montaz stoi OBOK `constraints`, a nie w nich: tamte jada do silnika jako
              filtry doboru, a to jest wylacznie pozycja kosztowa w podsumowaniu. */}
          <CheckBox checked={montazWliczony} label={t('kreator.ograniczenia.montaz')}
            onChange={(e) => { setMontazWliczony(e.target.checked); zapiszDaneProjektu({ montazWliczony: e.target.checked }); }} />
        </div>

        {/* CTA (desktop) */}
        <div className="mt-6 hidden md:flex items-center gap-3">
          <button type="button" onClick={onSubmit} disabled={!canSubmit || loading}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-colors ${
              canSubmit && !loading ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}>
            <IkonaAsystenta className="w-4 h-4" />
            {loading ? t('kreator.przyciski.dobieramy') : t('kreator.przyciski.dobierz')}
          </button>
          <button type="button" onClick={onReset}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
            <FontAwesomeIcon icon={faRotateLeft} />
            {t('kreator.przyciski.reset')}
          </button>
          {!canSubmit && (
            <span className="text-xs text-gray-400">
              {selectedCats.length === 0 ? t('kreator.brakuje.kategoria')
                : !budgetOk ? t('kreator.brakuje.budzet')
                  : !kwpOk ? t('kreator.brakuje.moc')
                    : t('kreator.brakuje.pola')}
            </span>
          )}
        </div>
      </div>

      {/* WYNIKI */}
      <div ref={resultsRef} className="mt-8">
        {loading ? (
          <div className="grid lg:grid-cols-2 gap-5">{[0, 1].map((i) => <SkeletonCard key={i} />)}</div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700 flex items-center justify-between gap-3 flex-wrap">
            <span>{error}</span>
            <button type="button" onClick={onSubmit} className="font-semibold underline shrink-0">{t('kreator.przyciski.ponow')}</button>
          </div>
        ) : noResults ? (
          <NoResults data={noResults} onRelax={onRelax} onRaiseBudget={onRaiseBudget}
                      kosztyWlasneZl={kosztyDodatkoweTeraz} />
        ) : variants.length > 0 ? (
          <div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
              <h2 className="text-xl font-bold text-gray-900">{t('kreator.wyniki.naglowek')} <span className="text-gray-400 font-normal">({variants.length})</span></h2>
              <p className="text-xs text-gray-500">
                {t('kreator.wyniki.budzet', { kwota: state.budget_any ? t('kreator.budzet.dowolny') : pln(state.budget_total_pln) })}
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-5">
              {/* `indeks` steruje stroną dymka z propozycją zamiennika: siatka jest
                  dwukolumnowa, więc karty w lewej kolumnie wypuszczają dymek w lewo,
                  w prawej — w prawo. Inaczej dymek zasłaniałby sąsiedni wariant. */}
              {wariantyWidoczne.map((v, indeks) => (
                <VariantCard key={v.variant} variant={v} indeks={indeks}
                             konstrukcja={konstrukcja} montazWliczony={montazWliczony}
                             requestPayload={ostatniPayload} nazwaWstepna={pokazNazwe ? nazwaZestawu : ''} klient={klient} />
              ))}
              <ZestawyZaKontem ukryte={wariantyUkryte} onZalozKonto={() => setOknoKonta('zestawy')} />
            </div>
            <PreferenceCostsNote items={result?.preference_costs} onRaiseBudget={onRaiseBudget}
                                 kosztyWlasneZl={kosztyDodatkoweTeraz} />
            <p className="text-xs text-gray-500 mt-5 bg-white border border-gray-200 rounded-xl p-4">
              {t('kreator.wyniki.zastrzezenie')}
            </p>
          </div>
        ) : null}
      </div>

      {/* Okno „Załóż darmowe konto” (FE-08): po limicie doboru albo z karty ukrytych zestawów. */}
      {oknoKonta && (
        <OknoRejestracji narzedzie="asystent" {...TEKSTY_OKNA_DOBORU[oknoKonta]} onZamknij={() => setOknoKonta(null)} />
      )}

      {/* Sticky CTA (mobile) */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-3 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]">
        <div className="min-w-0">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">{t('kreator.budzet.krotko')}</p>
          <p className="text-sm font-bold text-gray-900 truncate">{state.budget_any ? t('kreator.budzet.dowolny') : pln(state.budget_total_pln)}</p>
        </div>
        <button type="button" onClick={onSubmit} disabled={!canSubmit || loading}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
            canSubmit && !loading ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'
          }`}>
          <IkonaAsystenta className="w-4 h-4" />
          {loading ? t('kreator.przyciski.dobieramy') : t('kreator.przyciski.dobierz')}
        </button>
      </div>
    </div>
  );
};

export default KitAssistant;
